import React, { useState } from 'react';
import { Shield, Clock, AlertCircle, X, Calendar, UserCheck, UserPlus } from 'lucide-react';
import { PARTICIPANT_STATUS } from '../../constants';
import { isActiveParticipant } from '../../utils/participantUtils';
import Avatar from '../ui/Avatar.jsx';
import Badge from '../ui/Badge.jsx';
import Card from '../ui/Card.jsx';
import Modal from '../ui/Modal.jsx';

// Ein Status, ein Chip. Vorher stand derselbe Zustand bis zu dreimal in einer
// Zeile: als Zähler im Kartenkopf, als "Host"-Chip neben dem Namen und als
// Pille rechts — plus ein Schild-Abzeichen, das auf dem Profilbild klebte.
const STATUS_BADGES = {
  [PARTICIPANT_STATUS.VALIDATED]: { tone: 'success', icon: Shield, label: 'Validiert' },
  [PARTICIPANT_STATUS.RESERVED]: { tone: 'info', icon: Calendar, label: 'Reserviert' },
  [PARTICIPANT_STATUS.PENDING]: { tone: 'warning', icon: Clock, label: 'Ausstehend' },
  [PARTICIPANT_STATUS.REJECTED]: { tone: 'danger', icon: AlertCircle, label: 'Abgelehnt' },
};

// Confirmation Modal für Kick — auf ui/Modal.Confirm (Focus-Trap + Escape gratis)
const KickConfirmationModal = ({ isOpen, onClose, onConfirm, participantName }) => (
  <Modal.Confirm
    open={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    title="Teilnehmende entfernen?"
    confirmLabel="Entfernen"
    variant="danger"
  >
    <div className="flex items-center gap-3 mb-2">
      <div className="w-12 h-12 rounded-full bg-danger-soft flex items-center justify-center flex-shrink-0">
        <AlertCircle className="h-6 w-6 text-danger" />
      </div>
      <p className="text-sm text-muted-foreground">
        Möchtest du <span className="font-semibold">{participantName}</span> wirklich aus der Session entfernen?
      </p>
    </div>
  </Modal.Confirm>
);

// Altdaten stehen teils klein ('validated'), deshalb jeder Statusvergleich normalisiert
const statusOf = (participant) => String(participant?.status || '').toUpperCase();

const ParticipantsList = ({
  participants, maxParticipants, isHost, currentUserId,
  onKickParticipant, onMarkPresent, onReadmitParticipant,
}) => {
  const [kickConfirmation, setKickConfirmation] = useState({ isOpen: false, participant: null });

  const handleKickClick = (participant) => {
    setKickConfirmation({ isOpen: true, participant });
  };

  const handleKickConfirm = () => {
    if (kickConfirmation.participant && onKickParticipant) {
      // 🔧 FIX: Use userId (required by backend API) with fallback to id
      onKickParticipant(kickConfirmation.participant.userId || kickConfirmation.participant.id);
    }
    setKickConfirmation({ isOpen: false, participant: null });
  };

  const handleKickCancel = () => {
    setKickConfirmation({ isOpen: false, participant: null });
  };
  // LEFT/KICKED/REJECTED nicht anzeigen oder zaehlen - das Backend liefert sie
  // als Audit-Trail mit, sie belegen aber keinen Platz mehr.
  const participantsList = (participants || []).filter(isActiveParticipant);

  // Entfernte Teilnehmer sieht nur der Host - damit ein versehentlicher Kick
  // ueberhaupt korrigierbar ist (der Gekickte kommt selbst nicht zurueck).
  const kickedList = isHost && onReadmitParticipant
    ? (participants || []).filter(p => statusOf(p) === PARTICIPANT_STATUS.KICKED)
    : [];

  const validatedCount = participantsList.filter(p => statusOf(p) === PARTICIPANT_STATUS.VALIDATED).length;
  const reservedCount = participantsList.filter(p => statusOf(p) === PARTICIPANT_STATUS.RESERVED).length;
  const pendingCount = participantsList.filter(p => statusOf(p) === PARTICIPANT_STATUS.PENDING).length;

  // Ein ruhiger Satz statt drei farbiger Schnipsel: Die Farbe sitzt in den
  // Chips der Zeilen, der Kopf zählt nur noch.
  const summary = [
    `${validatedCount} validiert`,
    reservedCount > 0 ? `${reservedCount} reserviert` : null,
    pendingCount > 0 ? `${pendingCount} ausstehend` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 p-3 sm:p-4 border-b border-border">
        <h3 className="text-sm sm:text-base font-medium text-foreground">
          Teilnehmende ({participantsList.length}/{maxParticipants || '∞'})
        </h3>
        <p className="text-xs text-muted-foreground">{summary}</p>
      </div>

      <ul className="divide-y divide-border">
        {participantsList.map((participant) => {
          const badge = STATUS_BADGES[statusOf(participant)];
          const canMarkPresent = isHost && !participant.isHost && onMarkPresent
            && (statusOf(participant) === PARTICIPANT_STATUS.PENDING
              || statusOf(participant) === PARTICIPANT_STATUS.RESERVED);
          const canKick = isHost && !participant.isHost
            && String(participant.id) !== String(currentUserId);

          return (
            <li
              key={participant.id}
              className="flex items-center gap-3 p-3 sm:p-4 hover:bg-surface-raised transition-colors"
            >
              {/* `shrink-0` am Avatar und `min-w-0` an der Textspalte: Ohne beides
                  gibt in einer Flex-Zeile das Bild nach, sobald rechts Chip und
                  Knöpfe stehen — aus dem runden Profilbild wurde ein Ei. */}
              <Avatar src={participant.image} name={participant.name} size="md" className="shrink-0" />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm sm:text-base font-medium text-foreground">
                  {participant.name}
                </p>
                {/* Host ist eine Rolle, kein Zustand — sie steht bei den anderen
                    Stammdaten und nimmt dem Statuschip rechts nicht den Platz weg. */}
                <p className="truncate text-xs text-muted-foreground">
                  {participant.isHost ? 'Host · ' : ''}Level {participant.level || 1}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                {badge && (
                  <Badge tone={badge.tone} size="md" icon={badge.icon}>{badge.label}</Badge>
                )}

                {/* Anwesend setzen - nur Host, fuer noch nicht validierte Teilnehmer (z.B. ohne eigenes Geraet) */}
                {canMarkPresent && (
                  <button
                    onClick={() => onMarkPresent(participant.userId || participant.id)}
                    className="flex min-h-[40px] items-center gap-1 rounded-lg px-2.5 text-sm font-medium text-success hover:bg-success-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    title="Manuell als anwesend markieren"
                    aria-label={`${participant.name} manuell als anwesend markieren`}
                  >
                    <UserCheck className="h-4 w-4" aria-hidden="true" />
                    <span className="hidden sm:inline">Anwesend</span>
                  </button>
                )}

                {/* Kick Button - nur für Host sichtbar, nicht für den Host selbst */}
                {canKick && (
                  <button
                    onClick={() => handleKickClick(participant)}
                    className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg text-faint hover:bg-danger-soft hover:text-danger transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    title="Teilnehmende entfernen"
                    aria-label={`${participant.name} aus der Session entfernen`}
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
              </div>
            </li>
          );
        })}

        {participantsList.length === 0 && (
          <li className="p-6 sm:p-8 text-center">
            <p className="text-xs sm:text-sm text-muted-foreground">
              Noch keine Teilnehmenden in dieser Session
            </p>
          </li>
        )}
      </ul>

      {kickedList.length > 0 && (
        <div className="border-t border-border">
          <div className="px-3 pt-3 sm:px-4 sm:pt-4">
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Entfernt
            </h4>
          </div>
          <ul className="divide-y divide-border">
            {kickedList.map((participant) => (
              <li
                key={participant.id}
                className="flex items-center gap-3 p-3 sm:p-4 opacity-70"
              >
                <Avatar src={participant.image} name={participant.name} size="md" className="shrink-0 grayscale" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm sm:text-base font-medium text-foreground">
                    {participant.name}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    Kann nicht selbst zurückkommen
                  </p>
                </div>
                <button
                  onClick={() => onReadmitParticipant(participant.userId || participant.id)}
                  className="flex min-h-[40px] shrink-0 items-center gap-1 rounded-lg px-2.5 text-sm font-medium text-primary hover:bg-primary-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  title="Teilnehmende wieder zulassen"
                  aria-label={`${participant.name} wieder zur Session zulassen`}
                >
                  <UserPlus className="h-4 w-4" aria-hidden="true" />
                  <span>Wieder zulassen</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {maxParticipants && participantsList.length < maxParticipants && (
        <div className="p-3 sm:p-4 border-t border-border bg-surface-raised">
          <p className="text-center text-xs text-muted-foreground">
            {maxParticipants - participantsList.length} weitere Plätze verfügbar
          </p>
        </div>
      )}

      {/* Kick Confirmation Modal */}
      <KickConfirmationModal
        isOpen={kickConfirmation.isOpen}
        onClose={handleKickCancel}
        onConfirm={handleKickConfirm}
        participantName={kickConfirmation.participant?.name || ''}
      />
    </Card>
  );
};

export default ParticipantsList;
